import { useId } from "react";
import { siMeta, siTiktok, siWhatsapp, siMessenger, siInstagram, siFacebook, siGoogle, siClaude, siGooglegemini } from "simple-icons";
import type { AdPlatform, Channel, AiProvider } from "@digisubs/shared";
import { AppWindow, Sprout } from "lucide-react";
import { cn } from "@/lib/cn";

// Brand marks as inline SVG (paths from Simple Icons, CC0) with the brands' own colors.

interface LogoProps {
  className?: string;
  size?: number;
}

/** Blue brand marks turn white inside a primary (blue) button, where the brand color would be invisible. */
const ON_PRIMARY = "[[data-variant=primary]_&]:fill-white";

export function MetaLogo({ className, size = 20, mono }: LogoProps & { mono?: boolean }) {
  const id = useId().replace(/:/g, "");
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className} role="img" aria-label="Meta">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#0081FB" />
          <stop offset="1" stopColor="#0064E1" />
        </linearGradient>
      </defs>
      <path d={siMeta.path} fill={mono ? "currentColor" : `url(#${id})`} className={ON_PRIMARY} />
    </svg>
  );
}

export function TikTokLogo({ className, size = 20, mono }: LogoProps & { mono?: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className} role="img" aria-label="TikTok">
      {!mono ? (
        <>
          <path d={siTiktok.path} fill="#25F4EE" transform="translate(-0.7 -0.6)" />
          <path d={siTiktok.path} fill="#FE2C55" transform="translate(0.7 0.6)" />
        </>
      ) : null}
      <path d={siTiktok.path} fill="#FFFFFF" />
    </svg>
  );
}

export function GoogleAdsLogo({ className, size = 20 }: LogoProps) {
  return (
    <svg viewBox="0 0 192 192" width={size} height={size} className={className} role="img" aria-label="Google Ads">
      <line x1="96" y1="44" x2="146" y2="144" stroke="#3C8BD9" strokeWidth="44" strokeLinecap="round" />
      <line x1="96" y1="44" x2="52" y2="132" stroke="#FABC04" strokeWidth="44" strokeLinecap="round" />
      <circle cx="48" cy="146" r="23" fill="#34A853" />
    </svg>
  );
}

export function WhatsAppLogo({ className, size = 20 }: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className} role="img" aria-label="WhatsApp">
      <path d={siWhatsapp.path} fill="#25D366" />
    </svg>
  );
}

export function MessengerLogo({ className, size = 20 }: LogoProps) {
  const id = useId().replace(/:/g, "");
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className} role="img" aria-label="Messenger">
      <defs>
        <radialGradient id={id} cx="0.2" cy="1" r="1.2">
          <stop offset="0" stopColor="#0099FF" />
          <stop offset="0.6" stopColor="#A033FF" />
          <stop offset="0.9" stopColor="#FF5280" />
          <stop offset="1" stopColor="#FF7061" />
        </radialGradient>
      </defs>
      <path d={siMessenger.path} fill={`url(#${id})`} className={ON_PRIMARY} />
    </svg>
  );
}

export function InstagramLogo({ className, size = 20 }: LogoProps) {
  const id = useId().replace(/:/g, "");
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className} role="img" aria-label="Instagram">
      <defs>
        <radialGradient id={id} cx="0.3" cy="1.07" r="1.3">
          <stop offset="0" stopColor="#FDF497" />
          <stop offset="0.1" stopColor="#FDF497" />
          <stop offset="0.5" stopColor="#FD5949" />
          <stop offset="0.68" stopColor="#D6249F" />
          <stop offset="1" stopColor="#285AEB" />
        </radialGradient>
      </defs>
      <path d={siInstagram.path} fill={`url(#${id})`} />
    </svg>
  );
}

export function FacebookLogo({ className, size = 20 }: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className} role="img" aria-label="Facebook">
      <path d={siFacebook.path} fill="#0866FF" className={ON_PRIMARY} />
    </svg>
  );
}

export function GoogleLogo({ className, size = 20 }: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className} role="img" aria-label="Google">
      <path d={siGoogle.path} fill="#FFFFFF" />
    </svg>
  );
}

export function ClaudeLogo({ className, size = 20 }: LogoProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className} role="img" aria-label="Claude">
      <path d={siClaude.path} fill="#D97757" />
    </svg>
  );
}

export function GeminiLogo({ className, size = 20 }: LogoProps) {
  const id = useId().replace(/:/g, "");
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className} role="img" aria-label="Gemini">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#4796E3" />
          <stop offset="0.5" stopColor="#9168C0" />
          <stop offset="1" stopColor="#D96570" />
        </linearGradient>
      </defs>
      <path d={siGooglegemini.path} fill={`url(#${id})`} className={ON_PRIMARY} />
    </svg>
  );
}

/** Neutral monogram tile for brands without a usable public mark (OpenAI, carriers, gateways). */
export function Monogram({ text, color = "#2f6bff", size = 20, className }: { text: string; color?: string; size?: number; className?: string }) {
  return (
    <span
      className={cn("inline-flex shrink-0 items-center justify-center rounded-md font-bold text-white", className)}
      style={{ width: size, height: size, background: color, fontSize: Math.max(8, size * 0.42) }}
      aria-hidden
    >
      {text.slice(0, 2).toUpperCase()}
    </span>
  );
}

export function OpenAiLogo({ size = 20, className }: LogoProps) {
  return <Monogram text="AI" color="#10a37f" size={size} className={className} />;
}

export function AiProviderLogo({ provider, size = 20, className }: { provider: AiProvider; size?: number; className?: string }) {
  if (provider === "anthropic") return <ClaudeLogo size={size} className={className} />;
  if (provider === "gemini") return <GeminiLogo size={size} className={className} />;
  return <OpenAiLogo size={size} className={className} />;
}

export function PlatformLogo({ platform, size = 20, className }: { platform: AdPlatform | "organic"; size?: number; className?: string }) {
  if (platform === "meta") return <MetaLogo size={size} className={className} />;
  if (platform === "tiktok") return <TikTokLogo size={size} className={className} />;
  if (platform === "google") return <GoogleAdsLogo size={size} className={className} />;
  return <Sprout className={cn("text-success", className)} style={{ width: size, height: size }} aria-label="Organique" />;
}

export function ChannelLogo({ channel, size = 18, className }: { channel: Channel; size?: number; className?: string }) {
  if (channel === "whatsapp") return <WhatsAppLogo size={size} className={className} />;
  if (channel === "messenger") return <MessengerLogo size={size} className={className} />;
  if (channel === "instagram") return <InstagramLogo size={size} className={className} />;
  return (
    <span role="img" aria-label="Site web" className={cn("inline-flex shrink-0 items-center justify-center rounded-[28%] bg-[#2563eb] text-white", className)} style={{ width: size, height: size }}>
      <AppWindow style={{ width: size * 0.66, height: size * 0.66 }} strokeWidth={2.2} aria-hidden />
    </span>
  );
}

/** Small round channel badge for avatars (bottom right corner like the inbox screenshot). */
export function ChannelBadge({ channel, size = 16 }: { channel: Channel; size?: number }) {
  return (
    <span className="inline-flex items-center justify-center rounded-full bg-[#0b1325] p-[2px]" style={{ width: size + 4, height: size + 4 }}>
      <ChannelLogo channel={channel} size={size} />
    </span>
  );
}

/** DigiSubs cube mark (sidebar logo). */
export function DigiSubsMark({ size = 36, className }: LogoProps) {
  const id = useId().replace(/:/g, "");
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} className={className} aria-hidden>
      <defs>
        <linearGradient id={`${id}t`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#7cb8ff" />
          <stop offset="1" stopColor="#2f6bff" />
        </linearGradient>
        <linearGradient id={`${id}l`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2f6bff" />
          <stop offset="1" stopColor="#163aa8" />
        </linearGradient>
        <linearGradient id={`${id}r`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1d4ed8" />
          <stop offset="1" stopColor="#0c2470" />
        </linearGradient>
      </defs>
      <path d="M32 5 57 18.5 32 32 7 18.5Z" fill={`url(#${id}t)`} />
      <path d="M7 18.5 32 32v27L7 45.5Z" fill={`url(#${id}l)`} />
      <path d="M57 18.5 32 32v27l25-13.5Z" fill={`url(#${id}r)`} />
      <path d="M32 5 57 18.5 32 32 7 18.5Z" fill="none" stroke="#a9ccff" strokeOpacity=".35" />
    </svg>
  );
}
